import {
  type AIAgent,
  getAgentId,
} from "@p0security/shared/types/ai-inventory";
import { Flex, Spin } from "antd";

import { EmptyState } from "../EmptyState.tsx";
import {
  type AgentActivity,
  EMPTY_ACTIVITY,
} from "../hooks/useAgentActivity.ts";
import { AgentRow, type AgentStats } from "./AgentRow.tsx";

export type AgentListProps = {
  agents: AIAgent[];
  perAgentStats: Record<string, AgentStats>;
  activitiesByAgent: ReadonlyMap<string, AgentActivity>;
  loading?: boolean;
  onSelect: (agent: AIAgent) => void;
  onSecureUnmanaged: (agent: AIAgent) => void;
};

const EMPTY_STATS: AgentStats = {
  connectorSummary: { unmanaged: 0, total: 0 },
  riskSummary: { high: 0, total: 0 },
  severityCounts: { urgent: 0, high: 0, medium: 0, low: 0 },
};

export const AgentList: React.FC<AgentListProps> = ({
  agents,
  perAgentStats,
  activitiesByAgent,
  loading,
  onSelect,
  onSecureUnmanaged,
}) => {
  if (loading && agents.length === 0) {
    return (
      <Flex justify="center" style={{ padding: "48px 0" }}>
        <Spin />
      </Flex>
    );
  }
  if (agents.length === 0) {
    return (
      <EmptyState
        title="No agents discovered yet"
        hint="Run an assessment to populate this inventory."
      />
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {agents.map((agent) => {
        const id = getAgentId(agent);
        return (
          <AgentRow
            key={id}
            agent={agent}
            stats={perAgentStats[id] ?? EMPTY_STATS}
            activity={activitiesByAgent.get(id) ?? EMPTY_ACTIVITY}
            onSelect={onSelect}
            onSecureUnmanaged={onSecureUnmanaged}
          />
        );
      })}
    </div>
  );
};
